"use client";

import Box from "@/components/Box";
import XStack from "@/components/XStack";
import YStack from "@/components/YStack";
import SizeableText from "@/components/SizeableText";
import styles from "./PhoneMockup.module.css";

type PhoneMockupProps = {
  agentName: string;
  userMessage: string;
  agentResponse: string;
  agentDetail?: string;
  isAnimating?: boolean;
  animationDelayClass?: string;
};

export function PhoneMockup({
  agentName,
  userMessage,
  agentResponse,
  agentDetail,
  isAnimating = false,
  animationDelayClass,
}: PhoneMockupProps) {
  return (
    <Box className={styles.iphoneFrame}>
      {/* Dynamic Island */}
      <Box className={styles.dynamicIsland} />

      {/* Status Bar */}
      <XStack
        justifyContent="space-between"
        alignItems="center"
        className={styles.statusBar}
      >
        <SizeableText
          size="small"
          fontWeight="medium"
          className={styles.statusTime}
        >
          9:41
        </SizeableText>
        <XStack gap={1} alignItems="center">
          <StatusBarIcons />
        </XStack>
      </XStack>

      {/* Messages Header - iMessage style */}
      <XStack
        justifyContent="space-between"
        alignItems="center"
        className={`${styles.messagesHeader} ${isAnimating ? styles.fadeOut : styles.fadeIn}`}
      >
        {/* Back button with count */}
        <Box className={styles.backButton}>
          <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
            <path
              d="M7 1L1 7L7 13"
              stroke="#007AFF"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className={styles.backCount}>12</span>
        </Box>

        {/* Centered contact name */}
        <XStack alignItems="center" gap={1}>
          <span className={styles.contactName}>{agentName}</span>
          <svg width="6" height="10" viewBox="0 0 6 10" fill="none">
            <path
              d="M1 1L5 5L1 9"
              stroke="#8e8e93"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </XStack>

        {/* FaceTime button */}
        <Box className={styles.facetimeButton}>
          <svg width="22" height="16" viewBox="0 0 22 16" fill="none">
            <rect
              x="0.75"
              y="2.75"
              width="14.5"
              height="10.5"
              rx="2.25"
              stroke="#007AFF"
              strokeWidth="1.5"
            />
            <path
              d="M17 5.5L21 3V13L17 10.5"
              stroke="#007AFF"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Box>
      </XStack>

      {/* iMessage label and timestamp */}
      <YStack alignItems="center" gap={0} className={styles.messageTimestamp}>
        <span className={styles.imessageLabel}>iMessage</span>
        <span className={styles.timestampText}>Today 2:01 PM</span>
      </YStack>

      {/* Chat Content */}
      <YStack gap={2} className={styles.chatContent}>
        <Box
          className={`${styles.chatBubbleUser} ${isAnimating ? styles.messageAnimateOut : animationDelayClass ? `${styles.messageAnimateIn} ${animationDelayClass}` : ""}`}
        >
          <SizeableText size="small" className={styles.userBubbleText}>
            {userMessage}
          </SizeableText>
        </Box>
        <Box
          className={`${styles.chatBubbleAgent} ${isAnimating ? styles.messageAnimateOut : animationDelayClass ? `${styles.messageAnimateIn} ${styles.messageDelay2}` : ""}`}
        >
          <SizeableText size="small" className={styles.text}>
            {agentResponse}
          </SizeableText>
        </Box>
        {agentDetail && (
          <Box
            className={`${styles.chatBubbleAgent} ${isAnimating ? styles.messageAnimateOut : animationDelayClass ? `${styles.messageAnimateIn} ${styles.messageDelay3}` : ""}`}
          >
            <SizeableText size="small" className={styles.text}>
              {agentDetail}
            </SizeableText>
          </Box>
        )}
      </YStack>

      {/* Input bar */}
      <XStack className={styles.inputBar} alignItems="center" gap={2}>
        <Box className={styles.plusButton}>
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <circle cx="11" cy="11" r="10" stroke="#8e8e93" strokeWidth="1.5" />
            <path
              d="M11 6V16M6 11H16"
              stroke="#8e8e93"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </Box>
        <Box className={styles.inputField}>
          <span className={styles.inputPlaceholder}>iMessage</span>
        </Box>
      </XStack>
    </Box>
  );
}

function StatusBarIcons() {
  return (
    <>
      <svg width="17" height="12" viewBox="0 0 17 12" fill="currentColor">
        <path d="M1 4.5C1 3.67 1.67 3 2.5 3H3.5C4.33 3 5 3.67 5 4.5V10.5C5 11.33 4.33 12 3.5 12H2.5C1.67 12 1 11.33 1 10.5V4.5Z" />
        <path d="M6 3C6 2.17 6.67 1.5 7.5 1.5H8.5C9.33 1.5 10 2.17 10 3V10.5C10 11.33 9.33 12 8.5 12H7.5C6.67 12 6 11.33 6 10.5V3Z" />
        <path
          d="M11 1C11 0.17 11.67 -0.5 12.5 -0.5H13.5C14.33 -0.5 15 0.17 15 1V10.5C15 11.33 14.33 12 13.5 12H12.5C11.67 12 11 11.33 11 10.5V1Z"
          opacity="0.3"
        />
      </svg>
      <svg width="15" height="11" viewBox="0 0 15 11" fill="currentColor">
        <path d="M7.5 2.5C9.5 2.5 11.3 3.3 12.6 4.6L14 3.2C12.3 1.5 10 0.5 7.5 0.5C5 0.5 2.7 1.5 1 3.2L2.4 4.6C3.7 3.3 5.5 2.5 7.5 2.5Z" />
        <path d="M7.5 5.5C8.6 5.5 9.6 5.9 10.4 6.6L11.8 5.2C10.6 4.1 9.1 3.5 7.5 3.5C5.9 3.5 4.4 4.1 3.2 5.2L4.6 6.6C5.4 5.9 6.4 5.5 7.5 5.5Z" />
        <circle cx="7.5" cy="9" r="2" />
      </svg>
      <svg width="25" height="12" viewBox="0 0 25 12" fill="currentColor">
        <rect
          x="0.5"
          y="0.5"
          width="21"
          height="11"
          rx="2.5"
          stroke="currentColor"
          fill="none"
        />
        <rect x="2" y="2" width="17" height="8" rx="1" />
        <path d="M23 4V8C23.8 7.6 24 6.8 24 6C24 5.2 23.8 4.4 23 4Z" />
      </svg>
    </>
  );
}
